import { useState, useEffect, useCallback } from 'react'

/**
 * PWA install state + action.
 *
 * Chromium browsers fire `beforeinstallprompt` when the app is installable; we
 * stash it and expose `promptInstall()` so a button can trigger the native
 * prompt on demand (Chrome usually suppresses the automatic mini-infobar).
 * iOS Safari has no such event — the caller shows manual "Add to Home Screen"
 * instructions when `isIOS` is true and the app isn't already installed.
 *
 * Returns:
 *   canInstall   – a deferred prompt is available (show the Install button)
 *   isIOS        – iOS Safari (needs the manual add-to-home-screen hint)
 *   isStandalone – already running as an installed app (hide the affordance)
 *   installed    – app was installed during this session
 *   promptInstall() – trigger the native prompt; resolves to the outcome
 */
export default function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null)
  const [installed, setInstalled] = useState(false)

  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true)

  const isIOS =
    typeof window !== 'undefined' &&
    /iphone|ipad|ipod/i.test(window.navigator.userAgent) &&
    !window.MSStream

  useEffect(() => {
    const onBeforeInstallPrompt = (e) => {
      // Prevent the default mini-infobar so we can present our own button.
      e.preventDefault()
      setDeferredPrompt(e)
    }
    const onInstalled = () => {
      setInstalled(true)
      setDeferredPrompt(null)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return null
    deferredPrompt.prompt()
    const choice = await deferredPrompt.userChoice
    // A deferred prompt can only be used once.
    setDeferredPrompt(null)
    return choice?.outcome ?? null
  }, [deferredPrompt])

  return {
    canInstall: Boolean(deferredPrompt) && !isStandalone && !installed,
    isIOS,
    isStandalone,
    installed,
    promptInstall,
  }
}
